import { useEffect } from 'react';
import { useAgentChatStore } from './store';
import type { CardBusyReason } from './types';

/** Stable identity so the selector below never re-renders on a miss. */
const NO_REASONS: CardBusyReason[] = [];

/**
 * Report async work on a confirmation card for as long as `busy` holds.
 *
 * The card's editor and its Confirm button live in different subtrees
 * (the price manager sits inside the scoped `OrderDraftProvider`, the
 * button outside it), so "the quote is still loading" has to travel
 * through the chat store rather than a prop. Unmounting clears the flag
 * — a collapsed or discarded card must never leave the stack locked.
 */
export const useReportCardBusy = (
  confirmationId: string,
  reason: CardBusyReason,
  busy: boolean,
): void => {
  const setCardBusy = useAgentChatStore(s => s.setCardBusy);

  useEffect(() => {
    setCardBusy(confirmationId, reason, busy);
    return () => setCardBusy(confirmationId, reason, false);
  }, [setCardBusy, confirmationId, reason, busy]);
};

/** What this card is waiting on, in the order the work started. */
export const useCardBusyReasons = (confirmationId: string): CardBusyReason[] =>
  useAgentChatStore(s => s.busyCards[confirmationId] ?? NO_REASONS);

/** Any card in the stack mid-flight — gates the bulk Confirm all. */
export const useIsAnyCardBusy = (): boolean =>
  useAgentChatStore(s => Object.keys(s.busyCards).length > 0);
